import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactElement,
  RefAttributes,
  TouchEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Content } from '../content';
import { useIsMobile } from '../lib/hooks';
import { ListCell } from '../list-cell';

import styles from './toast.scss';
import { Variant } from './types';

type Props = RefAttributes<HTMLDivElement> &
  Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> & {
    duration: number;
    variant: Variant;
    text: string;
    subtitle?: string;
    icon?: string | ReactElement;
    inverted?: boolean;
    visible?: boolean;
    multiline?: boolean;
    rightIcon?: string | ReactElement;
    onHide: () => void;
    onClick?: () => void;
    button?: ReactElement;
    dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-toast';

export const Toast: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      duration,
      variant = 'default',
      text,
      subtitle,
      icon,
      inverted,
      visible,
      multiline,
      rightIcon,
      onHide,
      button,
      onClick,
      className,
      dataTestId = COMPONENT_ID,
      ...rest
    },
    ref,
  ) => {
    const isMobile = useIsMobile();
    const timeoutId = useRef(null);

    const [touchStartY, setTouchStartY] = useState(0);
    const [touchEndY, setTouchEndY] = useState(0);

    const handleHide = useCallback(() => {
      onHide();
      if (timeoutId.current) clearTimeout(timeoutId.current);
    }, [onHide]);

    const handleGesture = () => {
      if (touchEndY === touchStartY) {
        onClick?.();
        return;
      }
      if (touchEndY <= touchStartY && visible) {
        handleHide();
      }
    };

    const handleTouchStart = (e: TouchEvent<HTMLDivElement>) =>
      setTouchStartY(e.changedTouches[0].screenY);

    const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
      setTouchEndY(e.changedTouches[0].screenY);
      handleGesture();
    };

    const handleClick = () => {
      if (isMobile) return;
      onClick?.();
      handleHide();
    };

    useEffect(() => {
      if (visible && duration) {
        timeoutId.current = setTimeout(() => handleHide(), duration);
      }
      return () => clearTimeout(timeoutId.current);
    }, [duration, handleHide, visible]);

    const classes = classNames(className, styles.container, {
      [styles.visible]: visible,
      [styles.pointer]: typeof onClick === 'function',
      [styles.success]: variant === 'success',
      [styles.error]: variant === 'error',
    });

    return (
      <div
        data-component-name={COMPONENT_ID}
        data-testid={dataTestId}
        data-id={dataTestId}
        ref={ref}
        className={classes}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        {...rest}>
        <ListCell
          text={text}
          subtitle={subtitle}
          multiline={multiline}
          inverted={inverted}
          dataTestId={`${dataTestId}-list-cell`}
          icon={icon}
          buttons={button}
          content={<Content icon={rightIcon} dataTestId={`${dataTestId}-list-cell-content`} />}
        />
      </div>
    );
  },
);

Toast.displayName = 'Toast';
